import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Users, Rocket } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const icons = {
  0: Calendar,
  1: Users,
  2: Rocket,
};

const statsConfig = [
  { value: '4', suffix: '+', labelKey: 'stats.years' },
  { value: '1000', suffix: '+', labelKey: 'stats.youth' },
  { value: '8', suffix: '', labelKey: 'stats.startups' },
];

const AnimatedCounter = ({ value, suffix, isVisible }) => {
  const [count, setCount] = useState(0);
  const numericValue = parseInt(value.replace(/\D/g, ''), 10);

  useEffect(() => {
    if (!isVisible) return;

    let start = 0;
    const duration = 2000;
    const increment = numericValue / (duration / 16);

    const timer = setInterval(() => {
      start += increment;
      if (start >= numericValue) {
        setCount(numericValue);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [numericValue, isVisible]);

  return (
    <span className="text-5xl sm:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-[#4DFF88] to-[#4DD6FF] bg-clip-text text-transparent">
      {count.toLocaleString()}{suffix}
    </span>
  );
};

const StatsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="stats"
      ref={sectionRef}
      className="relative py-24 bg-[#060b1a]"
    >
      {/* Subtle top border glow */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#4DFF88]/50 to-transparent" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {statsConfig.map((stat, index) => {
            const IconComponent = icons[index];
            return (
              <div
                key={index}
                className="relative group"
              >
                {/* Card */}
                <div className="relative p-8 rounded-lg border border-white/10 bg-white/5 backdrop-blur-sm text-center transition-all duration-500 hover:border-[#4DFF88]/30 hover:bg-[#4DFF88]/5">
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-[#4DFF88]/0 to-[#4DD6FF]/0 group-hover:from-[#4DFF88]/10 group-hover:to-[#4DD6FF]/5 transition-all duration-500" />
                  
                  {/* Icon */}
                  <div className="relative mb-4 inline-flex items-center justify-center w-14 h-14 rounded-lg bg-[#4DFF88]/10 text-[#4DFF88]">
                    <IconComponent className="w-7 h-7" />
                  </div>

                  {/* Counter */}
                  <div className="relative mb-3">
                    <AnimatedCounter
                      value={stat.value}
                      suffix={stat.suffix}
                      isVisible={isVisible}
                    />
                  </div>

                  {/* Label */}
                  <p className="relative text-gray-400 text-lg font-medium">
                    {t(stat.labelKey)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Subtle bottom border glow */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#4DD6FF]/50 to-transparent" />
    </section>
  );
};

export default StatsSection;
